import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Pencil, ShieldOff, Undo2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, PrimaryButton, QuietButton, Modal, Select, EmptyState
} from "../../components/UI";
import { useAuth } from "../../contexts/AuthContext";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

// UK date + time format: DD/MM/YYYY HH:mm (24h). Returns "Never" for null.
function formatUKDateTime(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Never";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch {
    return "Never";
  }
}

// Sort priority: active first, then invited, then revoked. Alphabetical by
// name (case-insensitive) inside each bucket. Revoked users end up at the
// bottom of the table.
const STATUS_RANK = { active: 0, invited: 1, revoked: 2 };
function sortUsers(list) {
  return [...list].sort((a, b) => {
    const ra = STATUS_RANK[a.status] ?? 99;
    const rb = STATUS_RANK[b.status] ?? 99;
    if (ra !== rb) return ra - rb;
    const na = (a.name || a.email || "").toLowerCase();
    const nb = (b.name || b.email || "").toLowerCase();
    return na.localeCompare(nb, "en-GB");
  });
}

// Extract a human-readable message from an axios error. Prefers the 409
// detail message so lockout guards ("Cannot revoke the last active admin…")
// surface verbatim to the user, rather than a generic "Failed" toast.
function apiError(e, fallback = "Something went wrong") {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail) && d.detail.length && d.detail[0].msg)
    return d.detail[0].msg;
  return fallback;
}

/* ------------------------------------------------------------------ */
/* status + role chips                                                 */
/* ------------------------------------------------------------------ */

function StatusChip({ status }) {
  const map = {
    active:  { bg: "#E8EFE9", fg: "#2F4F3E", dot: "#2F4F3E", label: "Active"  },
    invited: { bg: "#FEF3E2", fg: "#B45309", dot: "#D97706", label: "Invited" },
    revoked: { bg: "#F1E7E7", fg: "#8B2E2E", dot: "#B04141", label: "Revoked" },
  };
  const s = map[status] || map.invited;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600 }}
      data-testid={`user-status-chip-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

function RoleChip({ role }) {
  const isAdmin = role === "admin";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded"
      style={{
        background: isAdmin ? "#EEF0F5" : "#F3F1EA",
        color: isAdmin ? "#2A3652" : "#5B4E2A",
        fontSize: 11,
        fontWeight: 600,
      }}
      data-testid={`user-role-chip-${role}`}
    >
      {isAdmin ? "Admin" : "User"}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Invite modal                                                        */
/* ------------------------------------------------------------------ */

function InviteUserModal({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [role, setRole]   = useState("user");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (open) { setEmail(""); setName(""); setRole("user"); setError(""); }
  }, [open]);

  const submit = async () => {
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Enter a valid email address."); return;
    }
    if (!name.trim()) {
      setError("Enter a name for this user."); return;
    }
    setSaving(true);
    try {
      const r = await api.post("/users", {
        email: email.trim(), name: name.trim(), role,
      });
      toast.success(`Invited ${r.data.email}`);
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      // Surface backend messages (e.g. "User X already exists") verbatim.
      setError(apiError(e, "Invite failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite user"
      testid="invite-user-modal"
      maxWidth={520}
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="invite-cancel-btn">
            Cancel
          </QuietButton>
          <PrimaryButton
            onClick={submit}
            disabled={saving}
            icon={UserPlus}
            data-testid="invite-submit-btn"
          >
            {saving ? "Inviting…" : "Send invite"}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-ink-3 mb-4" style={{ fontSize: 12.5 }}>
        Access to LuxTender is invite-only. The invited user must sign in with
        Google using this exact email address. They will appear as{" "}
        <span className="text-ink-2 font-medium">Invited</span> until their
        first successful sign-in.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Email" required>
          <Input
            value={email}
            onChange={setEmail}
            placeholder="name@example.com"
            data-testid="invite-email-input"
          />
        </Field>
        <Field label="Name" required>
          <Input
            value={name}
            onChange={setName}
            placeholder="Full name"
            data-testid="invite-name-input"
          />
        </Field>
        <Field label="Role">
          <Select
            value={role}
            onChange={setRole}
            options={[
              { value: "user",  label: "User"  },
              { value: "admin", label: "Admin" },
            ]}
            data-testid="invite-role-select"
          />
        </Field>
      </div>

      {error && (
        <div
          className="mt-4 px-3 py-2 rounded border"
          style={{ background: "#F1E7E7", borderColor: "#E1CDCD", color: "#8B2E2E", fontSize: 12.5 }}
          data-testid="invite-error"
        >
          {error}
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Edit modal                                                          */
/* ------------------------------------------------------------------ */

function EditUserModal({ open, onClose, user, isSelf, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && user) {
      setName(user.name || "");
      setRole(user.role || "user");
      setError("");
    }
  }, [open, user]);

  if (!user) return null;

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required."); return; }
    const payload = {};
    if (name.trim() !== user.name) payload.name = name.trim();
    if (role !== user.role) payload.role = role;
    if (Object.keys(payload).length === 0) { onClose?.(); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      // 409 lockout guards ("Cannot revoke or demote the last active admin…")
      // come through here — show the message verbatim.
      setError(apiError(e, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit user"
      testid="edit-user-modal"
      maxWidth={520}
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="edit-cancel-btn">Cancel</QuietButton>
          <PrimaryButton
            onClick={submit}
            disabled={saving}
            data-testid="edit-submit-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </PrimaryButton>
        </>
      }
    >
      <div className="mb-4 pb-3 border-b border-line" style={{ fontSize: 12.5 }}>
        <div className="text-ink-3">Editing</div>
        <div className="text-ink font-mono">{user.email}</div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" required>
          <Input
            value={name}
            onChange={setName}
            data-testid="edit-name-input"
          />
        </Field>
        <Field
          label="Role"
          hint={isSelf ? "Demoting yourself requires another active admin." : null}
        >
          <Select
            value={role}
            onChange={setRole}
            options={[
              { value: "user",  label: "User"  },
              { value: "admin", label: "Admin" },
            ]}
            data-testid="edit-role-select"
          />
        </Field>
      </div>

      {error && (
        <div
          className="mt-4 px-3 py-2 rounded border flex items-start gap-2"
          style={{ background: "#F1E7E7", borderColor: "#E1CDCD", color: "#8B2E2E", fontSize: 12.5 }}
          data-testid="edit-error"
        >
          <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Revoke confirmation modal                                           */
/* ------------------------------------------------------------------ */

function RevokeConfirmModal({ open, onClose, user, onRevoked }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) setError(""); }, [open]);
  if (!user) return null;

  const submit = async () => {
    setSaving(true); setError("");
    try {
      await api.delete(`/users/${user.id}`);
      toast.success(`Revoked ${user.email}`);
      onRevoked?.();
      onClose?.();
    } catch (e) {
      // Zero-admin / self-revoke lockout guards surface here.
      setError(apiError(e, "Revoke failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revoke user access"
      testid="revoke-user-modal"
      maxWidth={480}
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="revoke-cancel-btn">
            Cancel
          </QuietButton>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="revoke-confirm-btn"
            className="h-9 px-4 rounded-md font-medium text-white transition-colors"
            style={{
              background: saving ? "#B04141aa" : "#B04141",
              fontSize: 13,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Revoking…" : "Revoke access"}
          </button>
        </>
      }
    >
      <p className="text-ink-2 mb-3" style={{ fontSize: 13, lineHeight: 1.55 }}>
        Revoke access for{" "}
        <span className="font-medium text-ink">{user.name || user.email}</span>{" "}
        (<span className="font-mono text-ink-2">{user.email}</span>)?
      </p>
      <div
        className="px-3 py-2.5 rounded border"
        style={{ background: "#FEF3E2", borderColor: "#F0DCB9", color: "#7A4D0A", fontSize: 12.5, lineHeight: 1.5 }}
      >
        Their current session ends immediately. If they try to sign in again,
        they will be denied. The user record is preserved for audit history;
        you can re-invite the same email later to restore access.
      </div>
      {error && (
        <div
          className="mt-3 px-3 py-2 rounded border flex items-start gap-2"
          style={{ background: "#F1E7E7", borderColor: "#E1CDCD", color: "#8B2E2E", fontSize: 12.5 }}
          data-testid="revoke-error"
        >
          <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* main tab                                                            */
/* ------------------------------------------------------------------ */

export function UsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [reinvitingId, setReinvitingId] = useState(null);

  const isAdmin = currentUser?.role === "admin";

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const r = await api.get("/users");
      setUsers(r.data || []);
    } catch (e) {
      // 403 for non-admins is expected; handled below via isAdmin gate.
      if (e?.response?.status !== 403) {
        setLoadError(apiError(e, "Failed to load users"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const sorted = useMemo(() => sortUsers(users), [users]);

  const reinvite = async (u) => {
    setReinvitingId(u.id);
    try {
      await api.post("/users", { email: u.email, name: u.name, role: u.role });
      toast.success(`Re-invited ${u.email}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Re-invite failed"));
    } finally {
      setReinvitingId(null);
    }
  };

  /* -------- non-admin lockout ---------- */
  if (!isAdmin) {
    return (
      <Card className="p-8">
        <EmptyState
          icon={ShieldAlert}
          title="Admins only"
          hint="User management is restricted to administrators. Ask an admin to invite or edit users on your behalf."
        />
      </Card>
    );
  }

  /* -------- loading ---------- */
  if (loading) {
    return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading users…</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
              Users
            </h3>
            <p className="text-ink-3" style={{ fontSize: 12.5, maxWidth: 620 }}>
              LuxTender is invite-only. Only emails listed here can sign in with
              Google. Revoking a user preserves their record for audit history
              but ends their session immediately.
            </p>
          </div>
          <PrimaryButton
            icon={UserPlus}
            onClick={() => setInviteOpen(true)}
            data-testid="invite-user-btn"
          >
            Invite user
          </PrimaryButton>
        </div>

        {loadError && (
          <div
            className="mb-3 px-3 py-2 rounded border"
            style={{ background: "#F1E7E7", borderColor: "#E1CDCD", color: "#8B2E2E", fontSize: 12.5 }}
            data-testid="users-load-error"
          >
            {loadError}
          </div>
        )}

        {sorted.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="No users yet"
            hint="Invite the first user to get started."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-line">
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Last login</Th>
                  <Th className="text-right pr-1">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((u) => {
                  const isSelf = u.id === currentUser?.id;
                  const isRevoked = u.status === "revoked";
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-line last:border-b-0"
                      data-testid={`user-row-${u.id}`}
                      style={isRevoked ? { opacity: 0.72 } : undefined}
                    >
                      <Td>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-mono text-ink-2"
                            style={{ fontSize: 12.5 }}
                          >
                            {u.email}
                          </span>
                          {isSelf && (
                            <span
                              className="px-1.5 py-0.5 rounded"
                              style={{
                                background: "#E8EFE9",
                                color: "#2F4F3E",
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 0.3,
                              }}
                              data-testid={`user-self-marker-${u.id}`}
                              title="This is you"
                            >
                              YOU
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>{u.name}</Td>
                      <Td><RoleChip role={u.role} /></Td>
                      <Td><StatusChip status={u.status} /></Td>
                      <Td>
                        <span
                          className={
                            u.last_login
                              ? "text-ink-2 font-mono tnum"
                              : "text-ink-4 italic"
                          }
                          style={{ fontSize: 12.5 }}
                        >
                          {formatUKDateTime(u.last_login)}
                        </span>
                      </Td>
                      <Td className="text-right pr-1">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setEditTarget(u)}
                            className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1"
                            style={{ fontSize: 12 }}
                            data-testid={`edit-user-btn-${u.id}`}
                            title="Edit user"
                          >
                            <Pencil size={12} />
                            Edit
                          </button>
                          {isRevoked ? (
                            <button
                              onClick={() => reinvite(u)}
                              disabled={reinvitingId === u.id}
                              className="h-7 px-2 rounded text-ink-3 hover:text-accent hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-60"
                              style={{ fontSize: 12 }}
                              data-testid={`reinvite-user-btn-${u.id}`}
                              title="Re-invite"
                            >
                              <Undo2 size={12} />
                              {reinvitingId === u.id ? "Re-inviting…" : "Re-invite"}
                            </button>
                          ) : (
                            <button
                              onClick={() => setRevokeTarget(u)}
                              className="h-7 px-2 rounded text-ink-3 hover:text-note-red hover:bg-surface-2 inline-flex items-center gap-1"
                              style={{ fontSize: 12 }}
                              data-testid={`revoke-user-btn-${u.id}`}
                              title="Revoke access"
                            >
                              <ShieldOff size={12} />
                              Revoke
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={load}
      />
      <EditUserModal
        open={!!editTarget}
        user={editTarget}
        isSelf={editTarget?.id === currentUser?.id}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />
      <RevokeConfirmModal
        open={!!revokeTarget}
        user={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={load}
      />
    </div>
  );
}

/* small helpers to keep the table markup readable */
function Th({ children, className = "" }) {
  return (
    <th
      className={["text-left py-2 pr-3 col-head text-ink-3", className].join(" ")}
      style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td
      className={["py-2.5 pr-3 text-ink", className].join(" ")}
      style={{ fontSize: 13, verticalAlign: "middle" }}
    >
      {children}
    </td>
  );
}
